import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PlaylistVideo } from '@/store/playlist/types';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PrimeVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentVideo: PlaylistVideo;
  allVideos: PlaylistVideo[];
  accentColor: string;
  playlistLogo?: string;
  autoPlayNext?: boolean;
}

export function PrimeVideoModal({
  isOpen,
  onClose,
  currentVideo,
  allVideos,
  accentColor,
  playlistLogo,
  autoPlayNext = false,
}: PrimeVideoModalProps) {
  useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Find current video index
  useEffect(() => {
    const index = allVideos.findIndex(v => v.id === currentVideo.id);
    if (index !== -1) {
      setCurrentIndex(index);
    }
  }, [currentVideo, allVideos]);

  const currentVideoData = allVideos[currentIndex];
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < allVideos.length - 1;

  const handlePrevious = () => {
    if (hasPrevious) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleNext = () => {
    if (hasNext) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  // Auto-play next video on end (simulated)
  useEffect(() => {
    if (!autoPlayNext || !hasNext) return;

    // Simula fim do vídeo após 30 segundos (em produção, usar evento onEnded do player)
    const timer = setTimeout(() => {
      handleNext();
    }, 30000);

    return () => clearTimeout(timer);
  }, [currentIndex, autoPlayNext, hasNext]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent data-theme-isolated
        className="max-w-[90vw] lg:max-w-[1280px] p-0 bg-black/95 dark:bg-black/95 border-none overflow-hidden"
        style={{
          backdropFilter: 'blur(8px)',
        }}
      >
        {/* Header with Logo and Close */}
        <div className="absolute top-4 left-4 right-4 z-50 flex items-center justify-between">
          {playlistLogo && (
            <img src={playlistLogo} alt={translateInterfaceText("Logo")} className="h-8 w-auto" />
          )}
          <button
            onClick={onClose}
            className="ml-auto w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Video Player */}
        <div className="w-full bg-black" style={{ height: '500px' }}>
          {/* Mock Player - In production, replace with actual video player */}
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-900 to-black">
            <div className="text-center text-white space-y-4">
              <div className="text-6xl">🎬</div>
              <p className="text-lg">{translateInterfaceText("Player de Vídeo")}</p>
              <p className="text-sm text-gray-400">{translateInterfaceText("Vídeo ID:")}{" "}{currentVideoData?.videoId}</p>
            </div>
          </div>
        </div>

        {/* Content Below Video */}
        <div className="p-8 space-y-6">
          {/* Title */}
          <h2 className="text-white text-2xl font-bold">
            {currentVideoData?.episodeTitle || translateInterfaceText("Aula {value0}", { value0: (currentIndex + 1) })}
          </h2>

          {/* Description (if available) */}
          <p className="text-gray-300 text-base leading-relaxed">
            {translateInterfaceText("Descrição da aula será exibida aqui. Esta é uma descrição de exemplo para demonstrar o layout do modal.")}</p>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between pt-4 border-t border-white/10">
            <Button
              onClick={handlePrevious}
              disabled={!hasPrevious}
              variant="outline"
              className={cn(
                "h-11 px-6 text-white border-white/20 hover:border-white/40",
                !hasPrevious && "opacity-40 cursor-not-allowed"
              )}
              style={{
                backgroundColor: hasPrevious ? 'rgba(255,255,255,0.05)' : 'transparent',
              }}
            >
              <ChevronLeft className="w-5 h-5 mr-2" />
              {translateInterfaceText("Anterior")}</Button>

            <div className="text-white/60 text-sm font-medium">
              {currentIndex + 1} / {allVideos.length}
            </div>

            <Button
              onClick={handleNext}
              disabled={!hasNext}
              className={cn(
                "h-11 px-6 text-white",
                !hasNext && "opacity-40 cursor-not-allowed"
              )}
              style={{
                backgroundColor: hasNext ? accentColor : 'rgba(255,255,255,0.1)',
              }}
            >
              {translateInterfaceText("Próxima")}<ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
